import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { gsap, useGsap } from "@/lib/motion";
import { HEX_PATH, BOX_PATH } from "@/components/brand/Logo";

/* Closing scene: the logo's outline draws itself as you arrive — the same
   hexagon that opened the site now frames the invitation to ship. */
export default function CtaBand() {
  const { t, lang } = useLang();
  const c = t.cta;
  const isRtl = lang === "ar";
  const root = useRef(null);

  useGsap(
    () => {
      const paths = root.current.querySelectorAll(".cta-mark path");
      paths.forEach((p) => {
        const len = p.getTotalLength();
        gsap.fromTo(
          p,
          { strokeDasharray: len, strokeDashoffset: len },
          {
            strokeDashoffset: 0,
            ease: "none",
            scrollTrigger: { trigger: root.current, start: "top 85%", end: "center 50%", scrub: true },
          },
        );
      });
      gsap.from(".cta-reveal", {
        y: 36,
        opacity: 0,
        stagger: 0.1,
        duration: 1,
        scrollTrigger: { trigger: root.current, start: "top 70%", once: true },
      });
    },
    [lang],
    root,
  );

  return (
    <section ref={root} data-testid="cta-band" className="on-dark relative overflow-hidden bg-[var(--forest-950)]">
      <svg
        aria-hidden
        viewBox="-20 -20 712 816"
        className="cta-mark absolute -end-24 top-1/2 -translate-y-1/2 h-[140%] w-auto opacity-40 pointer-events-none"
      >
        <path d={HEX_PATH} fill="none" stroke="oklch(0.7 0.14 136)" strokeWidth="3" />
        <path d={BOX_PATH} fill="none" stroke="oklch(0.9 0.19 122)" strokeWidth="3" />
      </svg>
      <div className="container-x relative py-24 lg:py-36">
        <h2 className="cta-reveal type-display text-[clamp(2.2rem,5.6vw,4.75rem)] text-[var(--on-dark)] max-w-[16ch]">{c.title}</h2>
        <p className="cta-reveal mt-6 text-lg lg:text-xl text-[var(--on-dark-muted)] max-w-[46ch] leading-relaxed">{c.body}</p>
        <div className="cta-reveal mt-10 flex flex-wrap gap-3">
          <Link to="/contact" className="btn-primary h-14 px-8 rounded-full text-base gap-2 group">
            {c.quote}
            <ArrowRight
              className={`w-5 h-5 transition-transform duration-500 ease-out-expo ${isRtl ? "rtl-flip group-hover:-translate-x-1" : "group-hover:translate-x-1"}`}
              aria-hidden
            />
          </Link>
          <a
            href="https://wa.me/905521627900"
            target="_blank"
            rel="noopener noreferrer"
            className="h-14 px-8 rounded-full text-base font-bold inline-flex items-center border border-white/25 text-[var(--on-dark)] hover:border-[var(--signal)] hover:text-[var(--signal)] transition-colors duration-300"
          >
            <span>{c.whatsapp}</span>
            <span className="mx-2 opacity-40" aria-hidden>·</span>
            <bdi dir="ltr">+90 552 162 7900</bdi>
          </a>
        </div>
      </div>
    </section>
  );
}
